import React from 'react';
import { Pagination, Form } from 'react-bootstrap';
import './ImprovedPagination.css';

const ImprovedPagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showInfo = true,
  totalItems = 0,
  itemsPerPage = 10,
  onItemsPerPageChange,
  showItemsPerPageSelector = true,
  className = '',
  size = 'sm'
}) => {
  if (totalPages <= 1) return null;

  const maxVisiblePages = 5;
  const halfVisible = Math.floor(maxVisiblePages / 2);

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
    }

    // Add visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const renderPageInfo = () => {
    if (!showInfo || totalItems === 0) return null;

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="pagination-info">
        Showing <strong>{startItem}</strong> to <strong>{endItem}</strong> of <strong>{totalItems}</strong> entries
      </div>
    );
  };

  const visiblePages = getVisiblePages();

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage);
    }
  };

  return (
    <div className={`improved-pagination-container ${className}`}>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        {renderPageInfo()}
        
        {showItemsPerPageSelector && onItemsPerPageChange && (
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              Show:
            </span>
            <Form.Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              size="sm"
              style={{ width: 'auto', minWidth: '80px' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
            <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              per page
            </span>
          </div>
        )}
      </div>
      
      <Pagination size={size} className="improved-pagination mb-0">
        {/* First page */}
        <Pagination.First 
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          title="Go to first page"
        />
        
        {/* Previous page */}
        <Pagination.Prev 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Go to previous page"
        />
        
        {/* Page numbers */}
        {visiblePages.map((page, index) => {
          if (page === 'ellipsis-start') {
            return (
              <Pagination.Ellipsis 
                key={`ellipsis-start-${index}`}
                disabled 
                title="More pages available"
              />
            );
          }
          
          if (page === 'ellipsis-end') {
            return (
              <Pagination.Ellipsis 
                key={`ellipsis-end-${index}`}
                disabled 
                title="More pages available"
              />
            );
          }
          
          return (
            <Pagination.Item
                key={page}
                  active={page === currentPage}
                  onClick={() => handlePageChange(page)}
                  title={`Go to page ${page}`}
            >
              {page}
            </Pagination.Item>
          );
        })}
        
        {/* Next page */}
        <Pagination.Next 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Go to next page"
        />
        
        {/* Last page */}
        <Pagination.Last 
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Go to last page"
        />
      </Pagination>
    </div>
  );
};

export default ImprovedPagination;
